/**
 * 전체 매장 목록 조회 훅
 * storeApi.getStores()를 사용하여 통합 API 호출
 *
 * 하이브리드 위치 기반 검색:
 * 1. URL 쿼리 파라미터 (lat/lng) 우선
 * 2. Geolocation API로 위치 획득 시도 (완료될 때까지 대기)
 * 3. QR 매장 앵커 (#271): 사용자가 QR로 진입한 매장 좌표를 fallback으로 사용
 * 4. 위치 획득 실패 시 이름순 정렬로 폴백
 *
 * 중복 호출 방지:
 * - 위치 결정이 완료된 후 단 한 번만 API 호출
 * - useRef로 호출 상태 추적
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { storeApi, ApiClientError } from '@/lib/api';
import { isStoreOpen } from '@/lib/api/transformers';
import type { StoreWithOrganization } from '@/lib/api/types';
import type { Restaurant } from '@/types';
import { useGeolocation, getLocationFromSearchParams } from './useGeolocation';
import { getLocationAnchor } from '@/lib/locationAnchor';

interface UseAllStoresState {
  stores: StoreWithOrganization[];
  loading: boolean;
  error: Error | null;
  meta: {
    total: number;
    hasLocation: boolean;
    searchCenter?: { lat: number; lng: number };
    searchRadius?: number;
  } | null;
  /** 근처 매장이 없어서 전체 매장으로 폴백했는지 여부 */
  isFallbackToAll: boolean;
}

interface UseAllStoresReturn extends UseAllStoresState {
  restaurants: Restaurant[];
  refetch: () => Promise<void>;
  /** 현재 위치 정보 */
  location: { lat: number; lng: number } | null;
  /** 위치 요청 (Geolocation) */
  requestLocation: () => void;
}

interface UseAllStoresOptions {
  /** API 호출 활성화 여부 (기본: true). false면 API/Geolocation 호출 안 함 */
  enabled?: boolean;
}

/**
 * 매장 ID 기반 일관된 숫자 생성 (간단한 해시 함수)
 * 동일한 ID에 대해 항상 같은 값을 반환
 */
function hashId(id: number | string): number {
  const str = String(id);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Store를 Restaurant 형식으로 변환
 */
function storeToRestaurant(store: StoreWithOrganization): Restaurant {
  const categoryMap: Record<string, string> = {
    taco: 'tacos',
    burger: 'burgers',
    pizza: 'pizza',
    sushi: 'sushi',
    chicken: 'chicken',
    meat: 'meat',
    seafood: 'seafood',
    dessert: 'desserts',
    bakery: 'bread',
    vegan: 'vegetarian',
  };

  const nameLower = (store.name || '').toLowerCase();
  let category = 'all';
  for (const [key, value] of Object.entries(categoryMap)) {
    if (nameLower.includes(key)) {
      category = value;
      break;
    }
  }

  // API에서 실제 distance가 제공될 때만 표시한다.
  // menu#403 — es-MX 단위 표준 (km 소문자 + 숫자와 단위 사이 공백)
  const hash = hashId(store.id);
  let distance: string;
  if (store.distance !== undefined && store.distance !== null) {
    distance = `${store.distance.toFixed(1)} km`;
  } else {
    distance = '';
  }

  const rating = store.rating || (3.5 + ((hash % 16) / 10));
  const minTime = 15 + (hash % 20);
  const maxTime = minTime + 10 + ((hash >> 4) % 10);
  // menu#403 — '분' 한국어 잔존 시정. es-MX 표준 'min' (locale 무관 단위 표기).
  const deliveryTime = `${minTime}-${maxTime} min`;

  return {
    id: String(store.id),
    nameKey: `api_store_${store.id}`,
    cuisineKey: `api_store_${store.id}`,
    category,
    rating,
    deliveryTime,
    distance,
    imageUrl: store.image_url || `https://picsum.photos/seed/${store.id}/400/300`,
    isOpen: isStoreOpen(store.status, store.is_open_now),
    isFavorite: false,
    lat: store.latitude || undefined,
    lng: store.longitude || undefined,
    address: store.address,
    _name: store.name,
    _cuisine: store.organization?.name ?? '',
    pickup_requires_waitlist: store.pickup_requires_waitlist ?? false,
  } as Restaurant & { _name: string; _cuisine: string };
}

/**
 * 전체 매장 목록 조회 훅
 * 통합 API (GET /api/v1/stores) 사용
 *
 * @param options.enabled - false면 API/Geolocation 호출 안 함 (기본: true)
 */
export function useAllStores(options: UseAllStoresOptions = {}): UseAllStoresReturn {
  const { enabled = true } = options;

  const [searchParams] = useSearchParams();
  // enabled=false면 Geolocation 자동 요청도 하지 않음
  const geolocation = useGeolocation({ autoRequest: enabled });
  // 마지막으로 fetch한 위치 키 (중복 호출 방지 + URL 변경 시 재호출)
  const lastFetchedKeyRef = useRef<string | null>(null);

  const [state, setState] = useState<UseAllStoresState>({
    stores: [],
    loading: enabled, // enabled=false면 처음부터 loading=false
    error: null,
    meta: null,
    isFallbackToAll: false,
  });

  // URL 파라미터에서 위치 추출
  const urlLocation = useMemo(() => {
    return getLocationFromSearchParams(searchParams);
  }, [searchParams]);

  // 위치 결정: URL 파라미터 > Geolocation > QR 매장 앵커(#271)
  const location = useMemo(() => {
    // 1. URL 쿼리 파라미터 우선
    if (urlLocation) return urlLocation;

    // 2. Geolocation 결과
    if (geolocation.hasLocation && geolocation.lat && geolocation.lng) {
      return { lat: geolocation.lat, lng: geolocation.lng };
    }

    // 3. QR 매장 앵커 — GPS 거부/실패 시 사용자가 QR로 진입한 매장 좌표를 기준점으로 사용.
    //    `geolocation.loading`이 false가 된 뒤(권한 결정 또는 실패 확정)에만 앵커를 평가하여
    //    실제 GPS 결과가 도착하기 전 앵커가 GPS를 선점하지 않도록 보장.
    if (!geolocation.loading) {
      const anchor = getLocationAnchor();
      if (anchor) return { lat: anchor.lat, lng: anchor.lng };
    }

    return null;
  }, [urlLocation, geolocation.hasLocation, geolocation.lat, geolocation.lng, geolocation.loading]);

  // 위치 결정 완료 여부 (성공이든 실패든)
  const isLocationResolved = useMemo(() => {
    // URL에 위치가 있으면 바로 완료
    if (urlLocation) return true;
    // Geolocation 로딩 중이면 미완료
    if (geolocation.loading) return false;
    // Geolocation 완료 (성공 또는 실패)
    return true;
  }, [urlLocation, geolocation.loading]);

  // API 호출 함수 (위치를 인자로 받아 의존성 제거)
  const fetchStores = useCallback(async (loc: { lat: number; lng: number } | null) => {
    setState((prev) => ({ ...prev, loading: true, error: null, isFallbackToAll: false }));

    try {
      const response = await storeApi.getStores({
        lat: loc?.lat,
        lng: loc?.lng,
        per_page: 100,
        sort: loc ? 'distance' : 'name',
      });

      // 위치 기반 검색 결과가 0개면 전체 매장 재호출
      if (loc && response.data.length === 0) {
        const fallbackResponse = await storeApi.getStores({
          per_page: 100,
          sort: 'name',
        });

        setState({
          stores: fallbackResponse.data,
          loading: false,
          error: null,
          meta: {
            total: fallbackResponse.meta.total,
            hasLocation: false,
            searchCenter: undefined,
            searchRadius: undefined,
          },
          isFallbackToAll: true, // 폴백 플래그 설정
        });
        return;
      }

      setState({
        stores: response.data,
        loading: false,
        error: null,
        meta: {
          total: response.meta.total,
          hasLocation: !!loc,
          searchCenter: response.meta.search_center,
          searchRadius: response.meta.search_radius_km,
        },
        isFallbackToAll: false,
      });
    } catch (err) {
      let message = '매장 목록을 불러오는데 실패했습니다';
      if (err instanceof ApiClientError) {
        if (err.status === 0) message = '네트워크 연결을 확인해주세요';
        else if (err.status === 500) message = '서버 오류가 발생했습니다';
      }
      setState((prev) => ({
        ...prev,
        loading: false,
        error: new Error(message),
        isFallbackToAll: false,
      }));
    }
  }, []);

  // 위치 결정 완료 후 API 호출 (위치 변경 시 재호출)
  useEffect(() => {
    // enabled=false면 API 호출 안 함
    if (!enabled) return;

    // 위치 결정 중이면 대기
    if (!isLocationResolved) return;

    // 위치 키 생성 (null이면 'no-location')
    const locationKey = location ? `${location.lat},${location.lng}` : 'no-location';

    // 동일 위치로 이미 호출했으면 스킵
    if (lastFetchedKeyRef.current === locationKey) return;

    lastFetchedKeyRef.current = locationKey;
    fetchStores(location);
  }, [enabled, isLocationResolved, location, fetchStores]);

  // Store를 Restaurant 형식으로 변환
  const restaurants = useMemo(() => {
    return state.stores.map((store) => storeToRestaurant(store));
  }, [state.stores]);

  // 수동 refetch (현재 위치로 다시 호출)
  const refetch = useCallback(async () => {
    await fetchStores(location);
  }, [fetchStores, location]);

  return {
    ...state,
    restaurants,
    refetch,
    location,
    requestLocation: geolocation.requestLocation,
  };
}

/**
 * API 매장 데이터용 동적 번역 생성
 */
export function createApiStoreTranslations(
  stores: (Restaurant & { _name?: string; _cuisine?: string })[]
): Record<string, { name: string; cuisine?: string }> {
  const translations: Record<string, { name: string; cuisine?: string }> = {};

  for (const store of stores) {
    if (store._name) {
      translations[store.nameKey] = {
        name: store._name,
        cuisine: store._cuisine || '',
      };
    }
  }

  return translations;
}
